import React, { useEffect, useState } from 'react';
import CertificateSelector from 'components/certUploader/CertificateSelector';
import RadioButton from 'components/buttons/RadioButton';
import { AssetAssignment, AssetUsageType } from 'types/rest/mockServiceTypes';
import _ from 'lodash';
import { useIsMount } from 'hooks/useIsMountHook';
import { useDispatch } from 'react-redux';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import {
  addCertificateToAssetBundle,
  CertAssignment,
  getCurrentlyUsedAssetByConfigMode,
  getSelectedKeysFromAssetBundle,
  isTrustStoreCert,
  removeAssetsByConfigMode,
  resolveAssetUsageType,
  updateConfigurablePropertyInBundle,
} from 'utils/AssetBundleUtils';

export type AssetAssignmentBundle = {
  assets?: AssetAssignment[]; //Certificates / keystore users has manually selected
  configKeyAssets?: AssetAssignment[]; //Config key variable
};

interface SslConfigurationProps {
  disabled: boolean;
  assetsBundle: AssetAssignmentBundle;

  /** When assigned assets change */
  onAssignmentChanged: (assetBundle: AssetAssignmentBundle) => void;

  /** Config mode can be either client or server. Each version have different
   *  assignment types for assets when onAssignmentChanged() method is called. */
  configMode: SSlConfigMode;
  heading?: any;

  /**
   * Configuration property input.
   */
  allowConfigurationProperty?: boolean;

  /** When true, we show only ONE way SSL option*/
  disableTwoWaySSL?: boolean;
}

export enum SSlConfigMode {
  CLIENT,
  SERVER,
}

export enum SslConfig {
  NoSSL,
  ONE_WAY,
  TWO_WAY,
}

const SslConfiguration = (props: SslConfigurationProps) => {
  const isMount = useIsMount();
  const dispatch = useDispatch();
  const { configMode, assetsBundle, heading } = props;

  /**
   * Determines which SSL Config to use based on the usage type of currently selected certificate.
   * @param assetUsageType
   */
  const resolveInitialSslConfigBasedOnUsageType = (assetUsageType: AssetUsageType): SslConfig => {
    let sslConfig = SslConfig.NoSSL;
    //Server Mode
    if (configMode === SSlConfigMode.SERVER) {
      switch (assetUsageType) {
        case 'SERVER_KEYSTORE_TRUSTSTORE':
          sslConfig = SslConfig.TWO_WAY;
          break;
        case 'SERVER_KEYSTORE':
        case 'SERVER_KEYSTORE_CERT':
          sslConfig = SslConfig.ONE_WAY;
          break;
      }
    }
    //Client mode
    else if (configMode === SSlConfigMode.CLIENT) {
      switch (assetUsageType) {
        case 'CLIENT_KEYSTORE_TRUSTSTORE':
          sslConfig = SslConfig.TWO_WAY;
          break;
        case 'CLIENT_TRUSTSTORE_CERT':
          sslConfig = SslConfig.ONE_WAY;
          break;
      }
    }

    return sslConfig;
  };

  /**
   * Gets SSL Config (NoSSL, 2-Way, 1-Way based on the certificate assignment type passed through props)
   */
  const getSslConfigBasedOnProps = () => {
    const selectedCert = getCurrentlyUsedAssetByConfigMode(assetsBundle, 'assets', configMode);
    const configProperty = getCurrentlyUsedAssetByConfigMode(assetsBundle, 'configKeyAssets', configMode);

    //Return no ssl if nothing found in the bundle
    if (_.isNil(selectedCert) && _.isNil(configProperty)) {
      return SslConfig.NoSSL;
    }

    //First look at the config properties, if not empty, use it at first place
    let sslConfig: SslConfig;
    if (!_.isNil(configProperty)) {
      sslConfig = resolveInitialSslConfigBasedOnUsageType(configProperty.assetUsageType);
    } else if (!_.isNil(selectedCert)) {
      sslConfig = resolveInitialSslConfigBasedOnUsageType(selectedCert.assetUsageType);
    }
    return sslConfig;
  };

  //No SSL by default
  const [sslConfig, setSslConfig] = useState<SslConfig>(getSslConfigBasedOnProps());

  //If assigned certificates changes
  useEffect(() => {
    if (!isMount) {
      setSslConfig(getSslConfigBasedOnProps());
    }
  }, [props.assetsBundle]);

  const addCertificate = (id: number, name: string, type: AssetUsageType) => {
    const cert: CertAssignment = {
      certId: id,
      certificateName: name,
      usageType: type,
    };
    const bundle = addCertificateToAssetBundle(props.assetsBundle, cert);
    props.onAssignmentChanged(bundle);
  };

  const onRemoveByIdHandler = (id: number) => {
    const updatedAssets = assetsBundle.assets.filter(asset => asset.assetId !== id);
    props.onAssignmentChanged({ ...assetsBundle, assets: updatedAssets });
  };

  /**
   * When NO SSL option is set, we need to update asset bundle and remove all assignments which might related to current
   * type.
   */
  const setNoSslOption = () => {
    const updatedAssets = removeAssetsByConfigMode(assetsBundle, configMode);
    props.onAssignmentChanged(updatedAssets);
    setSslConfig(SslConfig.NoSSL);
  };

  const onAliasChangedHandler = (id: number, alias: string) => {
    //Change assignment of the alias
    const assetAssignments = _.cloneDeep(assetsBundle.assets);
    let indexToUpdate = -1;
    if (configMode === SSlConfigMode.SERVER) {
      indexToUpdate = _.findIndex(
        assetAssignments,
        (cert: AssetAssignment) => cert.assetId === id && _.startsWith(cert.assetUsageType, 'SERVER_'),
      );
    } else if (configMode === SSlConfigMode.CLIENT) {
      indexToUpdate = _.findIndex(
        assetAssignments,
        (cert: AssetAssignment) => cert.assetId === id && _.startsWith(cert.assetUsageType, 'CLIENT_'),
      );
    }

    if (indexToUpdate == -1) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot select alias'));
      console.error('Could not find index of following certificate id: ', id);
      return;
    }
    assetAssignments[indexToUpdate].alias = alias;
    props.onAssignmentChanged({ assets: assetAssignments });
  };

  const onUpdatePropertyChangeHandler = (propertyKey: string, type: SslConfig) => {
    const assetUsageType: AssetUsageType = resolveAssetUsageType(type, configMode);
    const updatedBundle = updateConfigurablePropertyInBundle(assetsBundle, propertyKey, assetUsageType);
    props.onAssignmentChanged(updatedBundle);
  };

  const onSelectHandler = (id: number, name: string, type: SslConfig) => {
    const usageType = resolveAssetUsageType(type, configMode);
    addCertificate(id, name, usageType);
  };

  /**
   * Gets a Certificate Selector component based on the current type and config mode.
   * @param type
   */
  const getCertificateSelector = (type: SslConfig) => {
    const assetBundleKeys = getSelectedKeysFromAssetBundle(assetsBundle, type, configMode);
    return (
      <div className={'ml-5'}>
        <CertificateSelector
          key={type}
          selectedCertificateId={assetBundleKeys.certificateId}
          selectedAlias={assetBundleKeys.selectedAlias}
          type={isTrustStoreCert(type, configMode) ? 'CERTIFICATE' : 'KEYSTORE'}
          className={'mb-4'}
          onRemoveCertificate={onRemoveByIdHandler}
          onSelectCertificate={(certId, certificateName) => onSelectHandler(certId, certificateName, type)}
          onSelectAlias={onAliasChangedHandler}
          disabled={props.disabled}
          allowConfigurationProperty={
            _.isNil(props.allowConfigurationProperty) ? true : props.allowConfigurationProperty
          }
          onUpdateConfigPropertyValue={value => {
            onUpdatePropertyChangeHandler(value, type);
          }}
          configPropertyValue={assetBundleKeys.configKeyValue}
        />
      </div>
    );
  };

  return (
    <div>
      {heading}
      <div className={'mb-3'}>
        <RadioButton
          isChecked={sslConfig === SslConfig.NoSSL}
          id={'redirectToLive'}
          label={'No Authentication'}
          onChange={setNoSslOption}
          disabled={props.disabled}
        />
      </div>

      {/* One Way SSL */}
      <div className={'mb-3'}>
        <RadioButton
          isChecked={sslConfig === SslConfig.ONE_WAY}
          id={'redirectToLive'}
          label={isTrustStoreCert(SslConfig.ONE_WAY, configMode) ? 'Client Truststore Certificate' : '1-way SSL'}
          onChange={() => setSslConfig(SslConfig.ONE_WAY)}
          disabled={props.disabled}
        />
        {sslConfig === SslConfig.ONE_WAY && getCertificateSelector(SslConfig.ONE_WAY)}
      </div>

      {/*Two Way SSL*/}
      {props.disableTwoWaySSL !== true && (
        <div className={'mb-3'}>
          <RadioButton
            isChecked={sslConfig === SslConfig.TWO_WAY}
            id={'redirectToLive'}
            label={'2-way SSL'}
            disabled={props.disabled}
            onChange={() => setSslConfig(SslConfig.TWO_WAY)}
          />
          {sslConfig === SslConfig.TWO_WAY && getCertificateSelector(SslConfig.TWO_WAY)}
        </div>
      )}
    </div>
  );
};

export default SslConfiguration;
